/**
 * Horizon 4.0 chrome.js — header behaviors only.
 * 1. Sticky scroll-up reveal for the main header row.
 * 2. Native mega-menu panel injection (replaces the Menu Creator
 *    mega-menu-bridge app embed): panels ship as inert <template>
 *    elements and are cloned into a single host on hover OR focus
 *    (>= 1000px), with aria-expanded kept in sync on the trigger.
 * 3. Mobile <details> drawer: scroll lock, close button, backdrop, Esc,
 *    focus move-in / containment / restore, and drill-down rows that
 *    clone the same panel templates on first open (< 1000px).
 * 4. Cart count bubble sync via the document 'cart:change' CustomEvent.
 *
 * Loaded with `defer` from sections/header.liquid, so the DOM is parsed
 * before this runs. No dependencies, no custom elements.
 */
(function () {
  'use strict';

  var docEl = document.documentElement;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* Panel payloads shared by the desktop host (section 2) and the mobile
     drawer (section 3). A product_panel block renders after the metaobject
     panels, so keeping the last template per title lets the block win. */
  var desktop = window.matchMedia('(min-width: 1000px)');
  var templates = {};
  document.querySelectorAll('template[data-panel-title]').forEach(function (t) {
    templates[t.getAttribute('data-panel-title')] = t;
  });

  /* Clone a panel template with its images right-sized. Menu Creator panel
     HTML points at original CDN files (2048px), but panel images render at
     most ~290px wide on desktop (panels cap at 1280px) and smaller in the
     drawer. Asking Shopify's CDN for a 600px rendition (enough for 2x
     screens) while the clone is still inert means the original is never
     requested. SVGs and image.liquid output (already has srcset) are left alone. */
  var clonePanel = function (tpl) {
    var panel = tpl.content.cloneNode(true);
    panel.querySelectorAll('img[src]:not([srcset])').forEach(function (img) {
      var src = img.getAttribute('src');
      if (!/cdn\.shopify\.com\/|\/cdn\/shop\//.test(src) || /\.svg(\?|$)|[?&]width=/.test(src)) return;
      img.setAttribute('src', src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=600');
    });
    return panel;
  };

  /* ------------------------------------------------------------
   * 1. Sticky scroll-up reveal
   * Header row is `position: sticky; top: 0` with a fixed
   * --header-height reserved in CSS (no measurement, no CLS).
   * Translate it away scrolling down, reveal on scroll up.
   * ------------------------------------------------------------ */
  var header = document.querySelector('.site-header[data-sticky]');
  if (header) {
    var lastY = window.scrollY;
    var ticking = false;

    var update = function () {
      var y = window.scrollY;
      if (y <= 0) {
        header.classList.remove('site-header--hidden');
      } else if (y > lastY + 2 && y > 240 && !docEl.classList.contains('drawer-open')) {
        header.classList.add('site-header--hidden');
      } else if (y < lastY - 2) {
        header.classList.remove('site-header--hidden');
      }
      lastY = y;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------
   * 2. Desktop mega-menu panels (>= 1000px only)
   * Nav <li> elements carry data-panel-title matched server-side
   * against published panel metaobject titles. Panel HTML stays
   * inert inside <template data-panel-title> until first hover,
   * then is cloned into the single [data-mega-panel] host.
   * ------------------------------------------------------------ */
  var host = document.querySelector('[data-mega-panel]');
  if (host) {
    /* The panel only exists at >= 1000px; at 750-999px the markup's static
       aria-haspopup/aria-expanded would promise a popup that never opens,
       so strip them whenever the desktop query does not match. Crossing
       below the breakpoint also resets the panel itself — CSS only visually
       suppresses it, and without hide() a rotate-and-back would re-show a
       panel whose trigger reports collapsed. (Called after hide() exists.) */
    var syncPanelAria = function () {
      if (!desktop.matches) hide();
      document.querySelectorAll('.site-nav__item[data-panel-title] .site-nav__link').forEach(function (a) {
        if (desktop.matches) {
          a.setAttribute('aria-haspopup', 'true');
          if (!a.hasAttribute('aria-expanded')) a.setAttribute('aria-expanded', 'false');
        } else {
          a.removeAttribute('aria-haspopup');
          a.removeAttribute('aria-expanded');
        }
      });
    };

    var hideTimer = 0;
    var current = '';
    var trigger = null;
    // Set while we restore focus to a trigger, so its focusin does not reopen.
    var restoring = false;

    var hide = function () {
      host.hidden = true;
      host.replaceChildren();
      current = '';
      if (trigger) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger = null;
      }
    };

    var scheduleHide = function () {
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, 80);
    };

    var show = function (title, link) {
      var tpl = templates[title];
      if (!tpl) return;
      clearTimeout(hideTimer);
      if (current !== title) {
        host.replaceChildren(clonePanel(tpl));
        current = title;
        /* Native product panels use snippets/image.liquid, whose <img> stays
           at opacity 0 until thumbhash.js marks the wrapper loaded. Template
           content is inert, so the load-time scan never saw these. */
        if (window.HorizonThumbHash) window.HorizonThumbHash.enhanceImages(host);
      }
      host.hidden = false;
      if (trigger && trigger !== link) trigger.setAttribute('aria-expanded', 'false');
      trigger = link || null;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    };

    syncPanelAria();
    if (desktop.addEventListener) desktop.addEventListener('change', syncPanelAria);
    else if (desktop.addListener) desktop.addListener(syncPanelAria); // Safari < 14

    document.querySelectorAll('.site-nav__item[data-panel-title]').forEach(function (li) {
      var link = li.querySelector('.site-nav__link');
      var title = li.getAttribute('data-panel-title');

      li.addEventListener('mouseenter', function () {
        if (desktop.matches) show(title, link);
      });
      li.addEventListener('mouseleave', function () {
        if (desktop.matches) scheduleHide();
      });

      /* Keyboard parity with the :focus-within CSS dropdowns: open on focus,
         stay open while focus is inside the item or the panel host. */
      li.addEventListener('focusin', function () {
        if (desktop.matches && !restoring) show(title, link);
      });
      li.addEventListener('focusout', function (e) {
        if (!desktop.matches) return;
        var next = e.relatedTarget;
        if (next && (li.contains(next) || host.contains(next))) return;
        scheduleHide();
      });

      /* The host sits after the nav in the DOM, so Tab from the trigger would
         skip the open panel — bridge into it instead. */
      if (link) {
        link.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab' || e.shiftKey || host.hidden || trigger !== link) return;
          var first = host.querySelector(FOCUSABLE);
          if (!first) return;
          e.preventDefault();
          first.focus();
        });
      }
    });

    host.addEventListener('mouseenter', function () {
      clearTimeout(hideTimer);
    });
    host.addEventListener('mouseleave', scheduleHide);

    /* Theme editor: a product_panel block's shopify_attributes sit on its inert
       <template>, so selecting the block opens that panel to show the settings. */
    if (window.Shopify && window.Shopify.designMode) {
      var isPanelTpl = function (el) {
        return !!(el && el.matches && el.matches('template[data-panel-title]'));
      };
      document.addEventListener('shopify:block:select', function (e) {
        if (!isPanelTpl(e.target) || !desktop.matches) return;
        var title = e.target.getAttribute('data-panel-title');
        var sel = window.CSS && CSS.escape ? CSS.escape(title) : title;
        var li = document.querySelector('.site-nav__item[data-panel-title="' + sel + '"]');
        show(title, li ? li.querySelector('.site-nav__link') : null);
      });
      document.addEventListener('shopify:block:deselect', function (e) {
        if (isPanelTpl(e.target)) hide();
      });
    }

    host.addEventListener('focusin', function () {
      clearTimeout(hideTimer);
    });
    host.addEventListener('focusout', function (e) {
      var next = e.relatedTarget;
      if (next && (host.contains(next) || next.closest('.site-nav__item[data-panel-title]'))) return;
      scheduleHide();
    });

    // Tab out of the last panel link continues through the nav, not past it.
    host.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || host.hidden || !trigger) return;
      var items = host.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      var edge = e.shiftKey ? items[0] : items[items.length - 1];
      if (document.activeElement !== edge) return;
      e.preventDefault();
      if (e.shiftKey) {
        trigger.focus();
        return;
      }
      var li = trigger.closest('.site-nav__item');
      var nextLink = li && li.nextElementSibling ? li.nextElementSibling.querySelector('.site-nav__link') : null;
      var after = nextLink;
      if (!after) {
        var actions = document.querySelector('.site-header__actions');
        var cands = actions ? actions.querySelectorAll(FOCUSABLE) : [];
        for (var ci = 0; ci < cands.length; ci++) {
          // skip hidden candidates (e.g. the search input while its form is display:none)
          if (cands[ci].offsetParent) { after = cands[ci]; break; }
        }
      }
      hide();
      if (after) after.focus();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || host.hidden) return;
      var back = trigger;
      var inside =
        back &&
        (host.contains(document.activeElement) ||
          (back.closest('.site-nav__item') || back).contains(document.activeElement));
      restoring = true;
      hide();
      if (inside) back.focus();
      restoring = false;
    });
  }

  /* ------------------------------------------------------------
   * 3. Mobile drawer (<details id="MenuDrawer">)
   * The <summary> toggles natively without JS; here we add scroll
   * lock, backdrop / close-button dismissal, Escape, and focus
   * handling (move in on open, contain Tab, restore on close).
   * ------------------------------------------------------------ */
  var drawer = document.getElementById('MenuDrawer');
  if (drawer) {
    var drawerToggle = drawer.querySelector('.menu-drawer__toggle');
    var drawerPanel = drawer.querySelector('.menu-drawer__panel');
    var subs = drawer.querySelectorAll('.menu-drawer__sub');

    /* Drill-down rows (snippets/menu-drawer-item.liquid). CSS hides the
       other rows while one is open; here the first open swaps the fallback
       child links for a clone of the desktop panel template, and every
       open starts the panel at the top. */
    var fillSub = function (sub) {
      var body = sub.querySelector('[data-drawer-body]');
      var tpl = templates[sub.getAttribute('data-panel-title')];
      if (!body || !tpl || body.hasAttribute('data-filled')) return;
      body.replaceChildren(clonePanel(tpl));
      body.setAttribute('data-filled', '');
      // Same reason as show(): image.liquid wrappers inside templates stay hidden until enhanced.
      if (window.HorizonThumbHash) window.HorizonThumbHash.enhanceImages(body);
    };

    subs.forEach(function (sub) {
      sub.addEventListener('toggle', function () {
        if (!sub.open) return;
        fillSub(sub);
        if (drawerPanel) drawerPanel.scrollTop = 0;
      });
    });

    // Rows hidden by an open drill-down (and closed <details> content) still
    // match the selector, so only count what is actually rendered.
    var drawerFocusables = function () {
      return Array.prototype.filter.call(
        drawerPanel.querySelectorAll(FOCUSABLE + ', summary'),
        function (el) {
          return el.getClientRects().length > 0;
        }
      );
    };

    drawer.addEventListener('toggle', function () {
      docEl.classList.toggle('drawer-open', drawer.open);
      // Reopening the drawer starts at the top-level list, not the last row.
      if (!drawer.open) {
        subs.forEach(function (sub) {
          sub.open = false;
        });
      }
      if (!drawerPanel) return;
      if (drawer.open) {
        var close = drawerPanel.querySelector('.menu-drawer__close') || drawerPanel.querySelector(FOCUSABLE);
        if (close) close.focus();
      } else if (
        drawerToggle &&
        (!document.activeElement ||
          document.activeElement === document.body ||
          drawerPanel.contains(document.activeElement))
      ) {
        // Closing hid the focused element (focus has not fallen back to
        // <body> yet when this runs); put focus back on the toggle.
        drawerToggle.focus();
      }
    });

    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-drawer-close]')) drawer.removeAttribute('open');
    });

    // Contain Tab inside the open panel (the page behind is inert to sighted
    // users via the backdrop, but stays tabbable otherwise).
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !drawer.open || !drawerPanel) return;
      var items = drawerFocusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // Escape steps back one level: an open row first, then the drawer.
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !drawer.open) return;
      var sub = drawer.querySelector('.menu-drawer__sub[open]');
      if (sub) {
        sub.open = false;
        var summary = sub.querySelector('summary');
        if (summary) summary.focus();
        return;
      }
      drawer.removeAttribute('open');
    });

    /* Theme editor below 1000px: selecting a product_panel block opens the
       drawer on that item's row (the desktop listener in section 2 bails
       when the desktop query does not match). */
    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener('shopify:block:select', function (e) {
        var t = e.target;
        if (desktop.matches || !t || !t.matches || !t.matches('template[data-panel-title]')) return;
        var title = t.getAttribute('data-panel-title');
        subs.forEach(function (sub) {
          if (sub.getAttribute('data-panel-title') !== title) return;
          drawer.open = true;
          sub.open = true;
        });
      });
    }
  }

  /* ------------------------------------------------------------
   * 4. Cart count bubble
   * Contract: after a successful AJAX add-to-cart any component
   * dispatches document.dispatchEvent(new CustomEvent('cart:change',
   * { detail: { item_count: N } })).
   * ------------------------------------------------------------ */
  document.addEventListener('cart:change', function (e) {
    var count = e.detail && e.detail.item_count;
    if (typeof count !== 'number') return;
    document.querySelectorAll('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  });
})();
